import { Client } from '../../../API';
import { ConflictHandler, RefetchOptions, MergeOptions } from '../../conflictResolution/types';
import {applyMerge} from "../applyMerge";

/**
 * Fields that should never be modified by merge (immutable or system-managed)
 */
const IMMUTABLE_FIELDS = ['id', 'ownerUserId', 'created_at', 'createdAt', '__typename'];

/**
 * Fields that should always come from current state (system-managed)
 */
const SYSTEM_FIELDS = ['version', 'updatedAt', 'deletedAt', '_lastChangedAt'];

/**
 * Conflict handler for Client entities.
 * Handles version conflicts during client updates with intelligent merging.
 * 
 * Merge Strategy:
 * 1. Start with complete currentState (all server fields)
 * 2. Apply user's intendedChanges except for immutable/system fields
 * 3. Always use currentState.version for retry
 * 
 */
export const clientConflictHandler: ConflictHandler<Client> = {
    entityType: 'Client',

    async refetch({ entityId, dataClientService, authService }: RefetchOptions): Promise<Client> {
        // Fallback refetch - server should provide currentState
        throw new Error(
            'Client refetch not implemented - server should provide currentState in conflict response'
        );
    },

    merge({ currentState, intendedChanges }: MergeOptions<Client>): Partial<Client> {
        const merged: Partial<Client> = {};

        // Start with current state
        Object.assign(merged, currentState);

        // Apply intended changes, respecting field categories
        applyMerge<Client>(merged, intendedChanges, IMMUTABLE_FIELDS, SYSTEM_FIELDS);

        // Ensure version is from current state (critical for retry)
        merged.version = currentState.version;

        return merged;
    },

    maxRetries: 3,

    shouldRetry(conflict, attemptNumber): boolean {
        // Don't retry if entity was deleted
        if (conflict.conflictType === 'ENTITY_DELETED') {
            return false;
        }
        // Don't retry beyond max attempts
        return attemptNumber < 3;
    }
};
